import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CrudSitesService } from '@features/admin/pages/crud-sites/crud-sites.service';
import { FileUploadControl } from '@iplab/ngx-file-upload';
import { ISourceSiteAttachmentViewModel } from '@services/cleanfill-api/cleanfill-api.generated';
import { SiteType } from '@view-models/enums/site-type.enum';
import { ConfirmDialogResult, ConfirmDialogType } from '@widgets/site-common/confirm-dialog/confirm-dialog.model';
import { ConfirmDialogService } from '@widgets/site-common/confirm-dialog/confirm-dialog.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'site-file-upload',
  templateUrl: './site-file-upload.component.html',
  styleUrls: ['./site-file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiteFileUploadComponent),
      multi: true
    }
  ]
})
export class SiteFileUploadComponent implements OnInit, ControlValueAccessor {
  @Input() type: SiteType;
  @Input() siteUniqueId: string;
  @Input() hint: string;
  maxUploadCount = 5;
  fileUploadControl: FileUploadControl;
  filesControl: FormControl;
  subscription = new Subscription();
  onTouch = () => {};

  constructor(private crudSitesService: CrudSitesService, private confirmDialogService: ConfirmDialogService) {
    this.type = SiteType.SOURCE_SITE;
    this.initControls();
  }

  ngOnInit(): void {}

  initControls(): void {
    this.filesControl = new FormControl('');
    this.fileUploadControl = new FileUploadControl().setListVisibility(false);
    const sub = this.fileUploadControl.valueChanges.subscribe(async files => {
      if (files.length > 0) {
        if (files[0].size > 10485760) {
          this.confirmDialogService.confirm(`Cannot upload a file bigger than 10mb`, '', ConfirmDialogType.Ok);
          this.fileUploadControl.clear();
          return;
        }
        const filenames = files.map(x => x.name);
        const duplicates = this.findDuplicates(filenames);

        if (duplicates.length > 0) {
          this.confirmDialogService.confirm(`Cannot upload duplicate file(s): ${duplicates.join(', ')}`, '', ConfirmDialogType.Ok);
        } else if (this.maxUploadCount && this.getFileCount(filenames) > this.maxUploadCount) {
          this.confirmDialogService.confirm(`Cannot upload more than ${this.maxUploadCount} documents`, '', ConfirmDialogType.Ok);
        } else {
          await this.uploadFiles(files);
        }
        this.fileUploadControl.clear();
      }
    });
    this.subscription.add(sub);
  }
  getFileCount(files) {
    const uploadedFiles = this.filesControl.value;
    return uploadedFiles?.length + files?.length;
  }
  findDuplicates(filenames: string[]) {
    const alreadyUploaded = this.filesControl.value.map(x => x?.fileName);
    const allFilenames = [...filenames, ...alreadyUploaded];
    return allFilenames.filter((item, index) => allFilenames.indexOf(item) !== index);
  }
  registerOnChange(fn): void {
    const sub = this.filesControl.valueChanges.subscribe(value => fn(value));
    this.subscription.add(sub);
  }

  registerOnTouched(fn): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.filesControl.disable() : this.filesControl.enable();
  }

  writeValue(value: ISourceSiteAttachmentViewModel[]): void {
    this.filesControl.setValue(value);
  }

  async uploadFiles(files: File[]): Promise<void> {
    let attachments;
    switch (this.type) {
      case SiteType.SOURCE_SITE:
        attachments = await this.crudSitesService.uploadSourceSiteAttachments(this.siteUniqueId, files);
        break;
      case SiteType.FILL_SITE:
        attachments = await this.crudSitesService.uploadFillSiteAttachments(this.siteUniqueId, files);
        break;
    }

    this.filesControl.setValue(attachments);
    this.fileUploadControl.setValue([]);
  }
  async deleteConfirmation(file: ISourceSiteAttachmentViewModel): Promise<void> {
    const choice = await this.confirmDialogService.confirm('Are you sure you want to Delete?', '', ConfirmDialogType.YesNo);
    if (choice === ConfirmDialogResult.Yes) {
      this.deleteFile(file);
    }
  }
  async deleteFile(file: ISourceSiteAttachmentViewModel): Promise<void> {
    switch (this.type) {
      case SiteType.SOURCE_SITE:
        await this.crudSitesService.deleteSourceSiteFile(file);
        break;
      case SiteType.FILL_SITE:
        await this.crudSitesService.deleteFillSiteFile(file);
        break;
    }

    const currentFiles = this.filesControl.value?.slice();
    if (!Array.isArray(currentFiles)) {
      return;
    }

    const fileIndex = currentFiles.findIndex(f => f.uniqueId === file.uniqueId);
    if (fileIndex < 0) {
      return;
    }

    currentFiles.splice(fileIndex, 1);
    this.filesControl.setValue(currentFiles);
  }
}
